import { PersonThingRangeResponse, ThingActivity, ThingEventSnapshot } from '@eagle/core-data-types';
import { ActivityAccumulatorSummary, FeatureTypes, NumberAccumulatorEventTypes, TrackingEventTypes } from '@eagle/data-function-types';
import { Stack, Typography } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { DateTime, Duration } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Contract, isNullable, isObject, isRecord, isString, maybeArray, maybeBoolean, maybeNumber } from 'validata';
import { useAuthenticated } from '../../auth';
import { evaluate } from '../../evaluator';
import { DISPLAY_ACTIVITIES_FEATURE_FLAG, TRUSTED_ACTIVITIES_RULES_FLAG } from '../../feature-flags';
import { usePromise, useUiTemplate } from '../../hooks';
import { I18n } from '../../providers';
import { Nullable, Undefinable } from '../../types';
import { DEFAULT_ACTIVITY_CARD } from '../../ui-templates/thing-activity-card';
import { ErrorMessage } from '../error-message';
import { useBoolFlag, useObjectFlag } from '../flags';
import { Icon } from '../icons';
import { EventLocationData } from '../map';
import { ActivityType, JourneyActivityData, SortBy } from './entity-card';
import { JourneyItem } from './entity-journey-types';
import { JourneyAccordion } from './journey-accordion';
import './journey-activity.css';
import { filterTrustedActivities, TrustedActivitiesRules, TrustedActivityRule } from './trusted-activities-filter';
import { useHistorySearch } from './use-history-search';
import { getActivityNotableEvents } from './util';

interface JourneyActivityProps {
  dateRangeFinish: Date;
  dateRangeStart: Date;
  filterBy: ActivityType[];
  journey: JourneyItem;
  setJourneyActivityData: Dispatch<SetStateAction<Record<string, Undefinable<JourneyActivityData>>>>;
  sortBy: SortBy;
  thingEvents: ThingEventSnapshot[];
  thingId: string;
}

export interface ExtendActivityAccumulatorSummary extends ActivityAccumulatorSummary {
  increase: number;
}

export interface ExtendActivityAccumulatorGeofenceEnter extends ActivityAccumulatorSummary {
  geofenceId: string;
}

const formatGapDuration = (start: Date, finish: Date): string => {
  const duration = DateTime.fromJSDate(finish).diff(DateTime.fromJSDate(start));
  const normalized = duration.normalize();
  const { hours, minutes, seconds } = normalized.shiftTo('hours', 'minutes', 'seconds');
  const handleZero = (number: number): Undefinable<number> => number < 1 ? undefined : Math.round(number);
  return Duration.fromObject(
    {
      hours: handleZero(hours),
      minutes: handleZero(minutes),
      seconds: handleZero(seconds),
    },
    { locale: I18n.getInstance().language },
  ).toHuman();
};

const trustedRuleContract: Contract<TrustedActivityRule> = {
  include: maybeBoolean(),
  allowedProvisioning: isNullable(maybeArray(isString())),
  maxDurationMinutes: isNullable(maybeNumber()),
  minDurationSeconds: isNullable(maybeNumber()),
  ongoingMaxMinutes: isNullable(maybeNumber()),
};

const trustedRulesContract: Contract<TrustedActivitiesRules> = {
  activityTypes: isRecord(isObject(trustedRuleContract)),
  defaults: isObject(trustedRuleContract),
};

const trustedRulesValidator = isObject(trustedRulesContract);

const TemplatedActivity: FC<{ thingActivity: ThingActivity; thingEvents: ThingEventSnapshot[]; allEvents: EventLocationData[]; template: unknown; loaded: boolean }> = ({ allEvents, thingActivity, thingEvents, template }) => {
  const { restClient } = useAuthenticated();
  const [menuElement, setMenuElement] = useState<Nullable<HTMLButtonElement>>();
  const [menuOpened, setMenuOpened] = useState<boolean>(false);

  const accumulator = useMemo(() => {
    const activityStart = DateTime.fromJSDate(thingActivity.start);
    const activityFinish = thingActivity.finish ? DateTime.fromJSDate(thingActivity.finish) : DateTime.now();

    const activityEvents = allEvents.filter((event) => {
      const eventTime = DateTime.fromJSDate(event.occurred);
      return eventTime >= activityStart && eventTime <= activityFinish;
    });

    const result: {
      summary?: ExtendActivityAccumulatorSummary;
      update?: ExtendActivityAccumulatorSummary;
      geofence?: ExtendActivityAccumulatorGeofenceEnter;
      fuelConsumed?: ExtendActivityAccumulatorSummary;
    } = {};

    for (const item of activityEvents) {
      const data = item.data as unknown as ExtendActivityAccumulatorSummary;

      if (!result.summary &&
        item.featureTypeId === FeatureTypes.NUMBER_ACCUMULATOR_V0 &&
        item.feature === 'distance/primary' &&
        item.eventTypeId === NumberAccumulatorEventTypes.SUMMARY
      ) {
        result.summary = data;
      }

      if (!result.update &&
        item.featureTypeId === FeatureTypes.NUMBER_ACCUMULATOR_V0 &&
        item.feature === 'distance/primary' &&
        item.eventTypeId === NumberAccumulatorEventTypes.UPDATE
      ) {
        result.update = data;
      }

      if (!result.geofence &&
        item.featureTypeId === FeatureTypes.TRACKING_V0 &&
        item.feature === 'tracking' &&
        item.eventTypeId === TrackingEventTypes.GEOFENCE_ENTER &&
        DateTime.fromJSDate(item.occurred).equals(activityStart)) {
        result.geofence = item.data as unknown as ExtendActivityAccumulatorGeofenceEnter;
      }
    }

    return result;
  }, [thingActivity.start, thingActivity.finish, allEvents]);

  const [thingPersons] = usePromise<PersonThingRangeResponse[]>(
    async () => {
      return restClient.personThing.getRangesByThing(
        thingActivity.thingId,
        thingActivity.start,
        thingActivity.finish || undefined,
      );
    },
    [restClient.personThing, thingActivity.finish, thingActivity.start, thingActivity.thingId],
  );
  const thingPerson = useMemo(() => thingPersons?.[0], [thingPersons]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setMenuElement(event.currentTarget);
    setMenuOpened(true);
  }, []);

  const handleMenuClose = useCallback((): void => {
    setMenuOpened(false);
    setMenuElement(null);
  }, []);

  const currentDateTime = DateTime.now();
  const startDateTime = DateTime.fromJSDate(thingActivity.start);
  const timelineEndTime = thingActivity.finish || (currentDateTime.diff(startDateTime, 'hours').hours > 24 ? startDateTime.plus({ hours: 24 }).toJSDate() : currentDateTime.toJSDate());
  const events = useMemo(() => getActivityNotableEvents(thingEvents, thingActivity), [thingEvents, thingActivity]);

  const templateData = useMemo(() => ({
    thingActivity,
    events,
    accumulator,
    thingPerson,
    handleMenuOpen,
    handleMenuClose,
    menuElement,
    menuOpened,
    timelineEndTime,
  }), [thingActivity, events, accumulator, thingPerson, handleMenuOpen, handleMenuClose, menuElement, menuOpened, timelineEndTime]);

  return <>{evaluate(template, templateData)}</>;
};

export const JourneyActivity: FC<JourneyActivityProps> = ({
  dateRangeFinish,
  dateRangeStart,
  filterBy,
  journey,
  setJourneyActivityData,
  sortBy,
  thingEvents,
  thingId,
}) => {
  const { restClient } = useAuthenticated();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const displayActivitiesFeature = useBoolFlag(DISPLAY_ACTIVITIES_FEATURE_FLAG);
  const trustedActivitiesRules = useObjectFlag<TrustedActivitiesRules>(TRUSTED_ACTIVITIES_RULES_FLAG, trustedRulesValidator);
  const rules = useMemo(() => {
    return displayActivitiesFeature && trustedActivitiesRules ? trustedActivitiesRules : null;
  }, [displayActivitiesFeature, trustedActivitiesRules]);
  const { template, loaded } = useUiTemplate('track-journey-activity', DEFAULT_ACTIVITY_CARD);
  const { eventData } = useHistorySearch();

  const [allActivities, thingActivitiesError, state] = usePromise(
    async () => {
      const response = await restClient.thingActivity.fetchAllByThing(
        thingId,
        dateRangeStart,
        dateRangeFinish,
      );
      return response.filter((item) => !!item.start) as ThingActivity[];
    },
    [dateRangeFinish, dateRangeStart, restClient.thingActivity, thingId],
  );

  const thingActivities = useMemo(() => filterTrustedActivities(allActivities || [], rules), [allActivities, rules]);

  const { earliestStartTimestamp, latestFinishTimestamp } = useMemo(() => {
    return (thingActivities ?? []).reduce<{ earliestStartTimestamp?: Date; latestFinishTimestamp?: Date }>((obj, item) => {
      let { earliestStartTimestamp, latestFinishTimestamp } = obj;

      if (!earliestStartTimestamp || item.start.getTime() < earliestStartTimestamp.getTime()) earliestStartTimestamp = item.start;
      if (item.finish && (!latestFinishTimestamp || (item.finish.getTime() > latestFinishTimestamp.getTime()))) latestFinishTimestamp = item.finish;
      return { earliestStartTimestamp, latestFinishTimestamp };
    }, {});
  }, [thingActivities]);

  const { startTime, endTime } = useMemo(() => {
    return {
      startTime: earliestStartTimestamp && earliestStartTimestamp.getTime() < journey.dateRange.startTime.getTime()
        ? earliestStartTimestamp
        : journey.dateRange.startTime,
      endTime: latestFinishTimestamp && latestFinishTimestamp.getTime() > journey.dateRange.endTime.getTime()
        ? latestFinishTimestamp
        : journey.dateRange.endTime,
    };
  }, [earliestStartTimestamp, journey.dateRange.endTime, journey.dateRange.startTime, latestFinishTimestamp]);

  const journeyEvents = useMemo(() => {
    if (!eventData) return [];

    const filteredJourney = eventData.filter((data) => data.journeyId === journey.id);

    if (filteredJourney.length === 0) {
      return [];
    }

    return filteredJourney[0].data.items;
  }, [eventData, journey.id]);

  useEffect(() => {
    setJourneyActivityData((prev) => ({
      ...prev,
      [journey.id]: {
        thingActivities: thingActivities || [],
        fetchEventStartTime: startTime,
        fetchEventEndTime: endTime,
      },
    }));
  }, [endTime, journey.id, setJourneyActivityData, startTime, thingActivities]);

  const filteredThingActivities = useMemo(() => {
    if (!thingActivities) return [];
    return thingActivities
      .filter((item) => filterBy.length ? filterBy.includes(item.activityType as ActivityType) : true);
  }, [filterBy, thingActivities]);

  const sortedThingActivities = useMemo(() => {
    return [...filteredThingActivities]
      .sort((a, b) => sortBy === SortBy.OLDEST_FIRST
        ? a.start.getTime() - b.start.getTime()
        : (b.finish || new Date()).getTime() - (a.finish || new Date()).getTime(),
      );
  }, [filteredThingActivities, sortBy]);

  const renderSeparator = useCallback((lastFinish: Date, nextStart: Date) => {
    const gapDuration = nextStart.getTime() - lastFinish.getTime();

    if (gapDuration < 5 * 60 * 1000) return <></>;

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Icon icon="Clock" data-testid="clock-icon" style={{ color: theme.palette.grey[400], height: '.8em', width: '.8em' }} />
        <Typography variant="subtitle2" ml=".7rem" mt=".9rem" color="text.secondary">
          <i>{formatGapDuration(lastFinish, nextStart)}</i>
        </Typography>
      </Stack>
    );
  }, [theme.palette.grey]);

  const renderSortedThingActivities = useCallback(() => {
    let lastFinishDateTime: Date | null = null;
    let lastStartDateTime: Date | null = null;

    return (
      <Stack spacing={2}>
        {sortedThingActivities.map((thingActivity, index, arr) => {
          if (
            sortBy === SortBy.OLDEST_FIRST && thingActivity.finish
            && (
              !lastFinishDateTime
              || lastFinishDateTime.getTime() < thingActivity.finish.getTime()
            )
          ) lastFinishDateTime = thingActivity.finish;
          if (
            sortBy === SortBy.LATEST_FIRST
            && (
              !lastStartDateTime
              || lastStartDateTime.getTime() > thingActivity.start.getTime()
            )
          ) lastStartDateTime = thingActivity.start;

          return <React.Fragment key={index}>
            <TemplatedActivity thingActivity={thingActivity} thingEvents={thingEvents} allEvents={journeyEvents} template={template} loaded={loaded} />
            {sortBy === SortBy.OLDEST_FIRST && lastFinishDateTime && arr[index + 1] && renderSeparator(lastFinishDateTime, arr[index + 1].start)}
            {sortBy === SortBy.LATEST_FIRST && lastStartDateTime && arr[index + 1] && renderSeparator(arr[index + 1].finish || new Date(), lastStartDateTime)}
          </React.Fragment>;
        })}
      </Stack>
    );
  }, [renderSeparator, sortBy, sortedThingActivities, thingEvents, loaded, template, journeyEvents]);

  useEffect(() => {
    if (!thingActivitiesError) return;
    enqueueSnackbar(<ErrorMessage error={thingActivitiesError} />, { variant: 'error' });
  }, [thingActivitiesError]);

  if (state !== 'resolved' || !thingActivities) return <></>;

  return (
    <JourneyAccordion
      expanded={expanded}
      startTime={startTime}
      endTime={endTime}
      onChange={() => {
        setExpanded((prev) => !prev);
      }}
    >
      {renderSortedThingActivities()}
    </JourneyAccordion>
  );
};
